import type { Role } from '@atlas/api-contract';

/**
 * The Phase 2 permission rules, as pure predicates (spec, Phase 2 matrix).
 *
 * These are a *convenience* layer only. Every one of them is enforced for real
 * in the API (spec R7.8); the UI uses them to hide or disable controls a user
 * cannot use, and nothing more. Keeping them pure and framework-free is what
 * lets `useCan` bind them to the current session without duplicating the rules.
 *
 * "own" throughout means the signed-in user is the record's creator. Ownership
 * is read from a resource as `createdBy?.id ?? createdById` (or the comment's
 * `author?.id ?? authorId`); pass that through {@link resourceOwnerId}.
 */

/** Create a project or a prototype: Designer or Admin. */
export const canCreateContent = (role: Role): boolean =>
  role === 'DESIGNER' || role === 'ADMIN';

/**
 * Edit or delete a project/prototype/version, or upload a version: Admin may
 * touch anything; a Designer only their own; a Commenter nothing.
 */
export const canModifyOwned = (
  role: Role,
  ownerId: string | null,
  currentUserId: string,
): boolean => {
  if (role === 'ADMIN') {
    return true;
  }

  if (role === 'DESIGNER') {
    return ownerId !== null && ownerId === currentUserId;
  }

  return false;
};

/**
 * Edit or delete a comment: Admin may touch anyone's, everyone else only their
 * own (spec matrix — "own" for Commenter and Designer, "any" for Admin).
 */
export const canModifyComment = (
  role: Role,
  authorId: string | null,
  currentUserId: string,
): boolean =>
  role === 'ADMIN' || (authorId !== null && authorId === currentUserId);

/** View and manage the Users screen: Admin only. */
export const canManageUsers = (role: Role): boolean => role === 'ADMIN';

/**
 * Ownership id of a resource that carries an embedded author summary, falling
 * back to the denormalised foreign key.
 */
export const resourceOwnerId = (resource: {
  createdBy?: { id: string } | null;
  createdById: string | null;
}): string | null => resource.createdBy?.id ?? resource.createdById;
